import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, TextField, Chip, Stack, Box, Button, Link, Stepper, Step, StepLabel, StepContent, Alert } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import toast from 'react-hot-toast';
import { queryApi } from 'src/api/queryApi';

const SuccessfulPlacementTab = ({ queryID, query, expert, refetch }) => {
  const [mediaURL, setMediaURL] = useState('');
  const [mediaURLError, setMediaURLError] = useState('');
  const [publicationTitle, setPublicationTitle] = useState('');
  const [publicationTitleError, setPublicationTitleError] = useState('');
  const [savedTitle, setSavedTitle] = useState('');
  const [savedURL, setSavedURL] = useState('');
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [emailSent, setEmailSent] = useState(false);

  const isStep1Complete = Boolean(savedURL);
  const isStep2Complete = isStep1Complete && Boolean(savedTitle);

  const parsePointsOfContact = (jsonData) => {
    try {
      return JSON.parse(jsonData);
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    const initialEmails = new Set();

    if (expert?.email && expert.email.trim() !== '') {
      initialEmails.add(expert.email);
    }

    if (expert?.pointOfContactJson) {
      const pocs = parsePointsOfContact(expert.pointOfContactJson);
      pocs.forEach((poc) => {
        if (poc.email && poc.email.trim() !== '') {
          initialEmails.add(poc.email);
        }
      });
    }

    setEmails([...initialEmails]);
  }, [expert]);

  useEffect(() => {
    const hasURL = Boolean(query?.mediaURL);
    const hasTitle = Boolean(query?.title);

    if (hasURL) {
      setMediaURL(query.mediaURL);
      setSavedURL(query.mediaURL);
    }
    if (hasTitle) {
      setPublicationTitle(query.title);
      setSavedTitle(query.title);
    }

    if (!hasURL) {
      setActiveStep(0);
    } else if (!hasTitle) {
      setActiveStep(1);
    } else {
      setActiveStep(2);
    }

    if (query?.status === 'successful') {
      setEmailSent(true);
    }
  }, [query]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    if (!newEmail) return;

    if (!validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!emails.includes(newEmail)) {
      setEmails([...emails, newEmail]);
      setNewEmail('');
      setEmailError('');
    } else {
      setEmailError('This email is already in the list');
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmails(emails.filter((email) => email !== emailToRemove));
  };

  const validateURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleURLChange = (e) => {
    const url = e.target.value;
    setMediaURL(url);
    if (url && !validateURL(url)) {
      setMediaURLError('Please enter a valid URL');
    } else {
      setMediaURLError('');
    }
  };

  const buildUpdatePayload = (overrides = {}) => ({
    queryID,
    title: query.title,
    description: query.description,
    status: query.status,
    source: query.source,
    topics: query.topics,
    mediaURL: query.mediaURL,
    mediaOutlet: query.mediaOutlet,
    queryURL: query.queryURL,
    dueDate: query.dueDate,
    expertsID: query.expertsID,
    emailSentOut: query.emailSentOut,
    userClaimID: query.userClaimID,
    createdBy: query.createdBy,
    notes: query.notes,
    ...overrides,
  });

  const handleSavePublicationURL = async () => {
    if (!mediaURL) {
      toast.error('Please add a publication URL');
      return;
    }

    if (!validateURL(mediaURL)) {
      setMediaURLError('Please enter a valid URL');
      return;
    }

    try {
      await queryApi.updateQueryV2(buildUpdatePayload({ mediaURL }));
      setSavedURL(mediaURL);
      setActiveStep(isStep2Complete ? 2 : 1);
      refetch();
      toast.success('Publication URL saved');
    } catch (error) {
      toast.error('Error saving publication URL');
    }
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setPublicationTitle(value);
    if (!value.trim()) {
      setPublicationTitleError('Please enter a publication title');
    } else {
      setPublicationTitleError('');
    }
  };

  const handleSavePublicationTitle = async () => {
    const trimmed = publicationTitle.trim();
    if (!trimmed) {
      setPublicationTitleError('Please enter a publication title');
      return;
    }

    try {
      await queryApi.updateQueryV2(buildUpdatePayload({ title: trimmed }));
      setSavedTitle(trimmed);
      setActiveStep(2);
      refetch();
      toast.success('Publication title saved');
    } catch (error) {
      toast.error('Error saving publication title');
    }
  };

  const handleGenerateEmail = async () => {
    if (!isStep1Complete || !isStep2Complete) {
      toast.error('Please complete previous steps first');
      return;
    }

    try {
      const response = await queryApi.successfulPlacement(queryID, expert.id, mediaURL, emails);

      if (!response.error) {
        setEmailSent(true);
        if (query.status === 'pending') {
          await queryApi.updateQuery(queryID, 'successful');
          refetch();
        }
        toast.success('Email sent successfully');
      } else {
        toast.error('Error sending email', response.status);
      }
    } catch (error) {
      toast.error('Error generating email:', error);
    }
  };

  const renderStepSummary = (value, isActive) => {
    if (!value || isActive) return null;
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
        {value}
      </Typography>
    );
  };

  return (
    <Box p={3}>
      <Stepper activeStep={activeStep} orientation="vertical">
        {/* Step 1: Publication URL */}
        <Step completed={isStep1Complete}>
          <StepLabel
            optional={renderStepSummary(savedURL, activeStep === 0)}
            onClick={isStep1Complete ? () => setActiveStep(0) : undefined}
            sx={isStep1Complete ? { cursor: 'pointer' } : undefined}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Add Publication URL
            </Typography>
          </StepLabel>
          <StepContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                placeholder="https://example.com/article"
                size="medium"
                fullWidth
                value={mediaURL}
                onChange={handleURLChange}
                error={!!mediaURLError}
                helperText={mediaURLError}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleSavePublicationURL} disabled={!mediaURL || !!mediaURLError}>
                  {isStep1Complete ? 'Update URL' : 'Save & Continue'}
                </Button>
                {isStep1Complete && (
                  <Button
                    variant="text"
                    onClick={() => {
                      setMediaURL(savedURL);
                      setMediaURLError('');
                      setActiveStep(isStep2Complete ? 2 : 1);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
            </Stack>
          </StepContent>
        </Step>

        {/* Step 2: Publication Title */}
        <Step completed={isStep2Complete}>
          <StepLabel
            optional={renderStepSummary(savedTitle, activeStep === 1)}
            onClick={isStep1Complete ? () => setActiveStep(1) : undefined}
            sx={isStep1Complete ? { cursor: 'pointer' } : undefined}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Add Publication Title
            </Typography>
          </StepLabel>
          <StepContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                Saving here overwrites the query title itself — it will be reflected throughout the app and used as the opportunity name in the
                success email.
              </Alert>
              <TextField
                placeholder="Publication Title"
                size="medium"
                fullWidth
                value={publicationTitle}
                onChange={handleTitleChange}
                error={!!publicationTitleError}
                helperText={publicationTitleError}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleSavePublicationTitle} disabled={!publicationTitle.trim() || !!publicationTitleError}>
                  {isStep2Complete ? 'Update Title' : 'Save & Continue'}
                </Button>
                {isStep2Complete && (
                  <Button
                    variant="text"
                    onClick={() => {
                      setPublicationTitle(savedTitle);
                      setPublicationTitleError('');
                      setActiveStep(2);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
            </Stack>
          </StepContent>
        </Step>

        {/* Step 3: Send Email */}
        <Step completed={emailSent}>
          <StepLabel
            optional={
              emailSent ? (
                <Typography variant="caption" sx={{ color: 'success.main' }}>
                  Placement marked successful
                </Typography>
              ) : null
            }
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Send Successful Email
            </Typography>
          </StepLabel>
          <StepContent>
            {emailSent ? (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Alert severity="success" variant="outlined">
                  This placement has already been marked successful.
                </Alert>
                <Box>
                  <Button variant="text" size="small" onClick={() => setEmailSent(false)}>
                    Send again
                  </Button>
                </Box>
              </Stack>
            ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {savedURL && (
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Publication
                  </Typography>
                  <Link href={savedURL} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all', fontSize: '0.9rem' }}>
                    {savedTitle || savedURL}
                  </Link>
                </Box>
              )}

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  Recipients
                </Typography>
                {emails.length > 0 && (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                    {emails.map((email) => (
                      <Chip key={email} label={email} onDelete={() => handleRemoveEmail(email)} color="primary" size="small" sx={{ py: 2, px: 1 }} />
                    ))}
                  </Stack>
                )}
                <Stack direction="row" spacing={1}>
                  <TextField
                    placeholder="Add email address"
                    size="small"
                    fullWidth
                    value={newEmail}
                    error={!!emailError}
                    helperText={emailError}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      setEmailError('');
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmail();
                      }
                    }}
                  />
                  <Button onClick={handleAddEmail} disabled={!newEmail} variant="outlined" sx={{ minWidth: 80 }}>
                    Add
                  </Button>
                </Stack>
              </Box>

              <Button variant="contained" startIcon={<SendIcon />} onClick={handleGenerateEmail} disabled={emails.length === 0} size="large">
                Send Email
              </Button>
            </Stack>
            )}
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};

SuccessfulPlacementTab.propTypes = {
  queryID: PropTypes.number.isRequired,
  query: PropTypes.object.isRequired,
  expert: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string,
    pointOfContactJson: PropTypes.string,
  }),
  refetch: PropTypes.func.isRequired,
};

export default SuccessfulPlacementTab;
